import { getLeaveSummary, listLeaves, listOvertime, type LeaveDto, type LeaveSummaryDto, type OvertimeDto } from '../../../api/hr';
import { listNotices, type NoticeListItem } from '../../../api/board';
import { listLiveSlides, type NoticeSlideDto, type NoticeSlideSetDto } from '../../../api/notice-slides';
import { getBroadcastWidget, type BroadcastChannelDto, type BroadcastVideoDto } from '../../../api/broadcast';
import { listMail, type MailListItem } from '../../../api/mail';
import { getMyProfile, type ProfileDto } from '../../../api/users';
import { listDocuments } from '../../../api/approval';
import { listEvents, type CalendarEventDto } from '../../../api/calendar';
import { listProjects, listTasks, type TaskDto } from '../../../api/tasks';
import { getReservationStatus, type ReservationStatusDto } from '../../../api/reservation';
import { listFolderChildren, type NodePublic } from '../../../api/workspace';
import { requestPresignedDownload } from '../../../api/files';
import { getTeamAttendance, type TeamAttendanceDto } from '../../../api/hr-team';
import { getDailyWork, type DailyWorkMonthDto } from '../../../api/attendance';
import { getAttendanceSummary, type AttendanceSummaryDto } from '../../../api/attendance';
import { getWorkPolicy, type WorkPolicyDto } from '../../../api/hr-schedule';
import type { ApprovalDocumentSummary } from '../../../types/approval';
import { useWidgetData, type WidgetDataState } from '../../../hooks/useWidgetData';

/**
 * 위젯 공용 쿼리 — **캐시 키의 단일 소유자**.
 *
 * 여러 위젯이 같은 데이터를 본다:
 *   · 캘린더 미니뷰 ↔ 일정 상세      → 같은 '이 달 일정'
 *   · 휴가 위젯     ↔ 근태 관리 위젯  → 같은 '연차 요약'
 *   · 공지 위젯     ↔ 다른 보드의 공지 위젯
 * 키를 각 위젯이 따로 지으면 문자열 한 글자 차이로 조용히 요청이 2배가 된다.
 * 그래서 키와 loader 를 여기 한 곳에 두고, 위젯은 훅만 호출한다.
 * 실제 합류(single-flight)와 TTL 은 `lib/widget-cache` 가 담당한다.
 */

const thisYear = () => new Date().getFullYear();

/** 연차 요약 — 휴가 위젯 · 근태 관리 위젯 · 근태 신청 모달이 공유. */
export function useLeaveSummary(): WidgetDataState<LeaveSummaryDto> {
  const year = thisYear();
  return useWidgetData(`hr:leave-summary:${year}`, () => getLeaveSummary(year));
}

/** 내 휴가 신청 내역. */
export function useLeaves(): WidgetDataState<LeaveDto[]> {
  return useWidgetData('hr:leaves', () => listLeaves());
}

/**
 * 내 프로필 — 소속·직급 (2026-09-16).
 *
 * ★★★ **`authStore` 의 `AuthUser` 를 쓰지 않는 이유.** 서버는 `/users/me` 에서 `position` 과
 *     `org.teamName` · `org.divisionName` 을 **이미 보내고 있다**. 그런데 그 칸을 `AuthUser` 로
 *     넓히면 **로그인 직후에는 비어 있다** — 로그인 응답(`LoginSuccess.user`)에는 그 칸이 없어서
 *     `setSession` 이 채울 수가 없고, 새로고침으로 probe 가 한 번 더 돌아야 채워진다.
 *     「로그인하면 소속이 없다가 새로고침하면 생긴다」는 화면은 고장으로 읽힌다.
 * ★★ `ProfileDto` 에는 두 칸이 **이미 형까지 다 있다** — 타입을 넓힐 것도 없다.
 * ★ 같은 `/users/me` 를 한 번 더 부르지만 `useWidgetData` 의 키·TTL 로 합류한다.
 */
export function useMyProfile(): WidgetDataState<ProfileDto> {
  return useWidgetData('users:me:profile', () => getMyProfile());
}

/**
 * 오늘의 근무제 (2026-09-16 · 시안의 「9월 23일 (수) · 유연근무」).
 *
 * ★★★ **한 번만 부른다.** 키가 날짜까지라 근태 · 근태 관리를 함께 얹어도 요청은 1회다
 *     (위젯마다 따로 부르면 한 판에 같은 요청이 여러 번 나간다 — 관제 조건).
 * ★ 못 받으면 `null` 이고, 화면은 **그 줄을 아예 안 그린다** — 빈 칸을 남기지 않는다.
 */
export function useWorkPolicy(enabled = true): WidgetDataState<WorkPolicyDto | null> {
  const today = new Date();
  const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return useWidgetData(`hr:work-policy:${ymd}`, async () => {
    try {
      return await getWorkPolicy(ymd);
    } catch {
      return null;   // 옛 서버 · 권한 없음 — 그 줄만 빠진다(다른 칸은 그대로 선다)
    }
  }, { enabled });
}

/** 초과근무 신청 내역. */
export function useOvertime(): WidgetDataState<OvertimeDto[]> {
  return useWidgetData('hr:overtime', () => listOvertime());
}

/**
 * 사내 공지.
 *
 * `enabled=false` 는 **관리 콘솔에서 메뉴를 껐다**는 뜻이다(2026-08-11). 이때는 요청 자체를
 * 보내지 않는다 — 서버가 404 MENU_DISABLED 로 막으므로 위젯에 '조회 실패' 만 남고, 대시보드에
 * 위젯 수만큼 헛된 404 가 쌓인다.
 */
export function useNotices(enabled = true): WidgetDataState<NoticeListItem[]> {
  return useWidgetData('board:notices', async () => (await listNotices()) ?? [], { enabled });
}

/** 받은 메일함 최근분. */
export function useInbox(enabled = true): WidgetDataState<MailListItem[]> {
  return useWidgetData('mail:inbox', async () => (await listMail('inbox', { limit: 50 })).messages ?? [], { enabled });
}

/** 내가 올린 전자결재 문서. */
export function useDraftedApprovals(enabled = true): WidgetDataState<ApprovalDocumentSummary[]> {
  return useWidgetData('approval:drafted', async () => (await listDocuments('drafted', { limit: 20 })).items, { enabled });
}

/** 이 달 일정 — 캘린더 미니뷰와 일정 상세가 함께 쓴다(요청 1회). */
export function useMonthEvents(year: number, month: number, enabled = true): WidgetDataState<CalendarEventDto[]> {
  const fromIso = new Date(year, month, 1).toISOString();
  const toIso = new Date(year, month + 1, 1).toISOString();
  return useWidgetData(`calendar:events:${fromIso}:${toIso}`, async () => {
    const res = await listEvents(fromIso, toIso);
    return (res.events ?? []).slice()
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  }, { enabled });
}

export type TaskWithProject = TaskDto & { projectName: string };

/**
 * 내 미완료 태스크.
 *
 * 기존 `ActiveTasksWidget` 은 프로젝트를 **순차 await** 로 돌며 태스크를 받아
 * 프로젝트 N개면 왕복 N번을 직렬로 기다렸다(프로젝트가 늘수록 선형으로 느려짐).
 * 여기서는 Promise.all 로 병렬화하고 결과를 캐시에 담는다.
 */
export function useMyTasks(myId: string, enabled = true): WidgetDataState<TaskWithProject[]> {
  return useWidgetData(
    `tasks:mine:${myId}`,
    async () => {
      const projects = (await listProjects()) ?? [];
      const perProject = await Promise.all(
        projects.map(async (p) => {
          try {
            const rows = (await listTasks(p.id)) ?? [];
            return rows.map((t) => ({ ...t, projectName: p.name }));
          } catch {
            return [] as TaskWithProject[];   // 한 프로젝트 실패가 위젯 전체를 죽이지 않도록.
          }
        }),
      );
      const rank: Record<string, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };
      return perProject
        .flat()
        .filter((t) => t.status !== 'DONE' && (t.assignees ?? []).includes(myId))
        .sort((a, b) => {
          const p = (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9);
          if (p !== 0) return p;
          return (a.dueDate ?? '9999-99-99') < (b.dueDate ?? '9999-99-99') ? -1 : 1;
        });
    },
    { enabled: enabled && myId !== '' },
  );
}

/**
 * 공지 슬라이드 — 위젯이 도는 목록 (migration 172).
 *
 * ★ 기한·대상 거르기와 최대 장수 자르기는 **서버가 이미 해서 준다.** 화면이 감추는 방식이면
 *   끝난 공지가 브라우저까지 갔다가 사라진다.
 * ★ 캐시 키를 여기 한 곳에 둔다 — 같은 묶음을 두 보드가 함께 봐도 요청은 한 번이다.
 */
export function useNoticeSlides(setId?: string): WidgetDataState<{ set: NoticeSlideSetDto | null; slides: NoticeSlideDto[] }> {
  return useWidgetData(`notice-slides:${setId ?? 'default'}`, () => listLiveSlides(setId));
}

/**
 * 사내 방송 — 위젯이 보여 줄 것 (migration 174).
 *
 * ★ 기한 거르기는 서버가 한다. 이어보기 지점도 같은 응답에 들어 있다 — 두 번 부르지 않는다.
 */
export function useBroadcast(): WidgetDataState<{ channel: BroadcastChannelDto | null; videos: BroadcastVideoDto[] }> {
  return useWidgetData('broadcast:widget', () => getBroadcastWidget());
}

/* ══════════════════════════════════════════════════════════════════════════
   판 5 — 풀사이즈 위젯(시안 화면 F) · 업무 위젯(시안 화면 L)이 보는 것
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * 오늘의 자원 현황 — 「회의실 현황」 (2026-09-09).
 *
 * ★ 왕복은 **하루에 한 번**이다(서버가 자원과 그날 예약을 함께 준다).
 * ⚠ 옛 서버에는 이 라우트가 없다 → 실패를 삼키고 위젯이 「조회 실패」를 적는다(값을 지어내지 않는다).
 */
export function useRoomStatus(category: string | undefined, enabled = true): WidgetDataState<ReservationStatusDto> {
  const day = new Date();
  const from = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const to = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
  return useWidgetData(
    `reservation:status:${from.toISOString().slice(0, 10)}:${category ?? 'all'}`,
    () => getReservationStatus(from, to, category),
    { enabled },
  );
}

/** 「포토월」이 그릴 사진 — 고른 폴더의 이미지 + 그때그때 서명한 주소. */
export interface WallPhoto { id: string; name: string; url: string }

/**
 * ★★★ 폴더를 **고르기 전에는 요청하지 않는다.** 어느 폴더인지 모르면 보여 줄 사진도 없다 —
 *     아무 폴더나 집어 「빈 그림을 지어내는」 위젯이 되면 안 된다(설계 §12 의 경고).
 * ⚠ 서명 주소는 5분짜리다. 위젯 캐시(60초) 안에서만 쓰이므로 만료 전에 그려진다.
 */
export function usePhotoWall(folderId: string | undefined, limit = 6): WidgetDataState<WallPhoto[]> {
  return useWidgetData(
    `workspace:photos:${folderId ?? 'none'}:${limit}`,
    async () => {
      const res = await listFolderChildren(folderId!, { kind: 'image', limit, sort: 'updated', order: 'desc' });
      const files = (res.children ?? []).filter((n) => n.type !== 'folder').slice(0, limit);
      const photos = await Promise.all(files.map(async (f) => {
        try {
          const { downloadUrl } = await requestPresignedDownload(f.id, { purpose: 'preview' }); // 사진 벽 = 「열람」(§3.4.1)
          return { id: f.id, name: f.docTitle || f.name, url: downloadUrl } as WallPhoto;
        } catch {
          return null;   // 한 장이 실패해도 나머지는 걸린다.
        }
      }));
      return photos.filter((p): p is WallPhoto => p !== null);
    },
    { enabled: Boolean(folderId) },
  );
}

/** 최근 문서 — 워크스페이스의 「최근 수정」(scope=home). 서버가 접근권한을 이미 지킨다. */
export function useRecentDocs(enabled = true, limit = 6): WidgetDataState<NodePublic[]> {
  return useWidgetData(
    `workspace:recent:${limit}`,
    async () => {
      const res = await listFolderChildren('root', { scope: 'home', limit, sort: 'updated', order: 'desc' });
      return (res.children ?? []).filter((n) => n.type !== 'folder').slice(0, limit);
    },
    { enabled },
  );
}

/**
 * 팀 근무 현황 — **팀장 · 회사 관리자만** (그 밖에는 서버가 403).
 *
 * ★★★ 403 을 「조회 실패」로 적으면 잘못된 안내다 — 못 보는 것이 아니라 **볼 자격이 아닌** 것이다.
 *     그래서 여기서 403 을 잡아 `null` 로 돌려주고, 위젯이 「팀장 · 회사 관리자만」이라 적는다.
 */
export function useTeamPresence(enabled = true): WidgetDataState<TeamAttendanceDto | null> {
  return useWidgetData(
    'hr:team-attendance:today',
    async () => {
      try {
        return await getTeamAttendance();
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 403 || status === 404) return null;
        throw e;
      }
    },
    { enabled },
  );
}

/** 이번 달 날별 근무 — KPI 스트립의 「평균 근무」가 본다. 옛 서버는 404 → 그 칸만 「—」. */
export function useMonthDailyWork(enabled = true): WidgetDataState<DailyWorkMonthDto | null> {
  const now = new Date();
  return useWidgetData(
    `hr:daily-work:${now.getFullYear()}-${now.getMonth() + 1}`,
    async () => {
      try {
        return await getDailyWork(now.getFullYear(), now.getMonth() + 1);
      } catch {
        return null;
      }
    },
    { enabled },
  );
}

/** 내가 결재해야 할 문서 — 「오늘 할 일」이 본다. */
export function useIncomingApprovals(enabled = true): WidgetDataState<ApprovalDocumentSummary[]> {
  return useWidgetData(
    'approval:incoming',
    async () => (await listDocuments('incoming', { limit: 20 })).items,
    { enabled },
  );
}

/** 이번 달 내가 끝낸 결재 — KPI 스트립. 더 있으면 `truncated` 로 알린다(「N+」로 적기 위해). */
export function useCompletedApprovals(enabled = true): WidgetDataState<{ items: ApprovalDocumentSummary[]; truncated: boolean }> {
  return useWidgetData(
    'approval:completed:100',
    async () => {
      const res = await listDocuments('completed', { limit: 100 });
      return { items: res.items, truncated: Boolean(res.nextCursor) };
    },
    { enabled },
  );
}

/* ── 대시보드 시안 v2 V3a (2026-09-16) — 설정을 읽는 위젯이 쓰는 쿼리 ──────────────────────
   ★ 키는 기존 것과 **같은 모양**으로 짓는다 — 받은편지함 · 올린 문서 · 이번 달 근무는 옛 위젯과 캐시를 나눈다. */

/** 메일함 최근분 — 받은편지함은 `useInbox` 와 같은 키 · 같은 요청. */
export function useMailFolder(folder: 'inbox' | 'all', enabled = true): WidgetDataState<MailListItem[]> {
  return useWidgetData(
    folder === 'inbox' ? 'mail:inbox' : 'mail:all',
    async () => (await listMail(folder, { limit: 50 })).messages ?? [],
    { enabled },
  );
}

/** 내가 올린 문서 — `useDraftedApprovals` 와 같은 키. 「내가 결재할 문서」만 볼 때는 부르지 않게 켜고 끈다. */
export function useDraftedApprovalsWhen(enabled: boolean): WidgetDataState<ApprovalDocumentSummary[]> {
  return useWidgetData('approval:drafted', async () => (await listDocuments('drafted', { limit: 20 })).items, { enabled });
}

/** 그 달 날별 근무 — 이번 달이면 `useMonthDailyWork` 와 같은 키. 옛 서버(404)는 null. */
export function useDailyWorkMonth(year: number, month1: number, enabled = true): WidgetDataState<DailyWorkMonthDto | null> {
  return useWidgetData(
    `hr:daily-work:${year}-${month1}`,
    async () => {
      try {
        return await getDailyWork(year, month1);
      } catch {
        return null;
      }
    },
    { enabled },
  );
}

/** 그 달의 근무일 · 지각 요약 (v2 V3b · 근태 관리 「근무일수 · 지각·조퇴」). 옛 서버 · 실패는 null. */
export function useAttendanceSummary(year: number, month1: number, enabled = true): WidgetDataState<AttendanceSummaryDto | null> {
  return useWidgetData(
    `hr:attendance-summary:${year}-${month1}`,
    async () => {
      try {
        return await getAttendanceSummary(year, month1);
      } catch {
        return null;
      }
    },
    { enabled },
  );
}

/** 내가 참여 중인 프로젝트 — KPI 스트립의 「진행 프로젝트」. */
export function useProjectCount(enabled = true): WidgetDataState<number> {
  return useWidgetData('tasks:project-count', async () => ((await listProjects()) ?? []).length, { enabled });
}
